import React from 'react';
import { NextPage } from 'next';
import { Box, makeStyles, Theme } from '@material-ui/core';
import { CONTACT_US_ROUTE, QUOTE_ROUTE } from 'data/routes';
import { Hero, SEO } from 'templates/layout';
import AboutDescriptionSection from 'templates/about-us/AboutDescriptionSection';
import { FeatureSection, AboutQuoteSection, TextImageSection } from 'templates/about-us';
import { CalendarTimeImage, InfoListImage, LocationImage, MoneyImage, TruckInfoWhiteImage } from 'svgs';
import { AboutUsBgMainMini, AboutUsBgMain } from 'images/inline-svg';
import { aboutUsBg, bgFeatureImage, trucksOnRoadImage } from 'images/about-us';

const useStyles = makeStyles((theme: Theme) => ({
  bgWrapper: {
    backgroundImage: `url("${AboutUsBgMainMini}")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 5%',
    backgroundSize: 'contain',
    [theme.breakpoints.up('sm')]: {
      backgroundPosition: 'right 10%',
      backgroundSize: '70%',
    },
    [theme.breakpoints.up('md')]: {
      backgroundImage: `url("${AboutUsBgMain}")`,
      backgroundPosition: 'left -250px',
    },
    [theme.breakpoints.up('lg')]: {
      backgroundSize: 'contain',
    },
  },
}));
const AboutUsPage: NextPage = () => {
  const classes = useStyles();

  return (
    <>
      <SEO
        title={'Explore the fastest-growing logistics company'}
        description={'Learn more about the company, its core values, and the main principles of operations.'}
      />
      <Hero
        title={
          <>
            <span>REDEFINING</span> <br /> LOGISTICS
          </>
        }
        buttonText="Get a Quote"
        text="Find out how we stand out from the crowd"
        image={aboutUsBg}
        link={QUOTE_ROUTE}
      />
      <TextImageSection
        title={{
          highlightWord: 'About',
          text: 'Patron Trucking',
        }}
        text="Patron Trucking is a brokerage company engaged in planning all freight delivery stages, carefully considering even the most seemingly insignificant details of the process. As a result, a professional approach and attention to detail can significantly reduce transportation time and cut costs for the client."
        linkBtn={{ link: CONTACT_US_ROUTE, text: 'Contact Us' }}
        image={trucksOnRoadImage}
        underTitleLineShow
      />
      <FeatureSection
        bgImage={bgFeatureImage}
        featuresList={[
          {
            svgIcon: <TruckInfoWhiteImage width="100%" viewBox="0 0 96 96" />,
            text:
              'Selecting a vehicle that is most suitable for transporting cargo with specific parameters: dimensions, weight, shelf life, explosion hazard, etc.',
          },
          {
            svgIcon: <LocationImage width="100%" viewBox="0 0 96 96" />,
            text:
              'Choosing the optimal route taking into account weather and seasonal phenomena, as well as minimizing various risks along the way.',
          },
          {
            svgIcon: <CalendarTimeImage width="100%" viewBox="0 0 96 96" />,
            text: 'Establishing the terms of freight transportation, taking into account the above parameters.',
          },
          {
            svgIcon: <MoneyImage width="100%" viewBox="0 0 96 96" />,
            text: 'Calculating the optimal cost, considering multiple options at the request of the customer.',
          },
          {
            svgIcon: <InfoListImage width="100%" viewBox="0 0 96 96" />,
            text:
              'A number of other less significant parameters, which together can have a substantial impact on the final decision.',
          },
        ]}
      />

      <Box className={classes.bgWrapper}>
        <AboutQuoteSection
          text="Thus, Patron Trucking is fully prepared to solve a whole range of interrelated tasks. We work with over 200 meticulously hand-picked carriers that deliver the most outstanding results within the very least amount of time possible. Our internal staff training and qualification programs ensure your inquiries are processed quickly, efficiently, and with care."
          linkBtn={{ text: 'Get a Quote', link: CONTACT_US_ROUTE }}
        />

        <AboutDescriptionSection
          headTitle={'OUR VALUES'}
          textContentList={[
            {
              title: 'Excellence and reliability',
              description:
                'By offering services at affordable prices while striving to provide quality work and make a good impression on customers, Patron Trucking fulfills any transportation or logistics orders.',
            },
            {
              title: 'Speed and high quality',
              description: 'The Patron Trucking company has put speed and quality as its central principles of operations.',
            },
            {
              title: 'Your logistics partner',
              description:
                'Patron Trucking remains the leading company in its sector, providing nationwide logistics services while not forgetting about respect and mutual understanding.',
            },
          ]}
        />
      </Box>
    </>
  );
};

export default AboutUsPage;
